import {
	AGILOX_API_ENDPOINT,
	App,
	APP_NAME,
	APP_SETTINGS_ADAPTER,
	AppNavMenuPointsService,
	ChunkLoadingFailedErrorHandler,
	ENVIRONMENT,
	FleetEventsService,
	headersInterceptor,
	initializeTranslations,
	localeProvider,
	MENU_POINTS,
	MenuPointService,
	PLAUSIBLE_DOMAIN,
	PlausibleService,
	provideDatepickerTranslationsWithNgxTranslate,
	provideSelectTranslationsWithNgxTranslate,
	provideTableSort,
	Role,
	SummaryOfTheYearService,
	SURVICATE_CONFIG,
	SurvicateService,
	ThemeService,
	TranslationsLoader,
	USER_FORMAT,
	provideFavicon,
} from '@agilox/common';
import { NavModule, ToastService, UserNotificationsService } from '@agilox/components';
import { CustomDatePipe } from '@agilox/pipe/custom-date';
import { CustomNumberPipe, provideNumberFormatter } from '@agilox/pipe/custom-number';
import { CustomTimePipe } from '@agilox/pipe/custom-time';
import { DatePipe, DecimalPipe } from '@angular/common';
import {
	HttpClient,
	provideHttpClient,
	withInterceptors,
	withInterceptorsFromDi,
	withXhr,
} from '@angular/common/http';
import {
	ApplicationConfig,
	ErrorHandler,
	importProvidersFrom,
	inject,
	provideAppInitializer,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, withHashLocation } from '@angular/router';
import { AUTH_CONFIG, AuthModule, MSALAUTHConfigFactory, userInitializationFactory } from '@auth';
import { MsalService } from '@azure/msal-angular';
import { environment } from '@my/env/environment';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { provideUserContext, UserStoreService } from '@user-store';
import { defaultIfEmpty, firstValueFrom, switchMap } from 'rxjs';
import { appRoutes } from './app.routes';
import { MyAppSettingsAdapter } from './my/general/adapters/my-app-settings.adapter';
import { createErrorHandlerInterceptor } from '@agilox/interceptors';
import { SubscriptionDatePipe } from '@agilox/pipe/custom-date';
import { MenuProcessingService } from './my/general/services/menu-processing.service';
import { NavigationService } from './my/general/services/navigation.service';
import { MaasService } from './my/pages/maas/maas.service';

export const appConfig: ApplicationConfig = {
	providers: [
		provideFavicon(),
		importProvidersFrom(
			BrowserModule,
			TranslateModule.forRoot({
				loader: {
					provide: TranslateLoader,
					useClass: TranslationsLoader,
					deps: [HttpClient],
				},
			}),
			AuthModule,
			NavModule
		),
		provideHttpClient(
			withXhr(),
			withInterceptors([
				createErrorHandlerInterceptor({ app: App.my, environment, onError: 'propagate' }),
				headersInterceptor,
			]),
			withInterceptorsFromDi() // needed for MSAL
		),
		provideRouter(appRoutes, withHashLocation()),
		provideStoreDevtools(),
		provideDatepickerTranslationsWithNgxTranslate(),
		provideSelectTranslationsWithNgxTranslate(),
		provideTableSort(),
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler({
				showDialog: false,
			}),
		},
		TranslateService,
		provideAppInitializer(() => {
			const initializerFn = initializeTranslations(inject(TranslateService));
			return initializerFn();
		}),
		{
			provide: Sentry.TraceService,
			deps: [Router],
		},
		{
			provide: ErrorHandler,
			useClass: ChunkLoadingFailedErrorHandler,
		},
		DatePipe,
		MaasService,
		{
			provide: MENU_POINTS,
			useFactory: () => {
				const menuProcessingService = inject(MenuProcessingService);
				return menuProcessingService.processedMenuPoints;
			},
		},
		MenuPointService,
		SummaryOfTheYearService,
		provideAppInitializer(() => {
			const initializerFn = () => {
				const traceService = inject(Sentry.TraceService);
				return () => {};
			};
			return Promise.resolve(initializerFn());
		}),
		provideAppInitializer(() => {
			const msalService = inject(MsalService);
			const userStore = inject(UserStoreService);
			const menuProcessingService = inject(MenuProcessingService);
			return firstValueFrom(
				userInitializationFactory(msalService, userStore, true)().pipe(
					defaultIfEmpty(null),
					switchMap(() => menuProcessingService.initializeMenuPoints())
				)
			);
		}),
		provideAppInitializer(() => {
			inject(ThemeService);
		}),
		MenuPointService,
		AppNavMenuPointsService,
		UserStoreService,
		...provideUserContext(),
		NavigationService,
		{
			provide: USER_FORMAT,
			useFactory: (userStore: UserStoreService) => userStore.userFormat,
			deps: [UserStoreService],
		},
		{
			provide: ENVIRONMENT,
			useValue: environment,
		},
		{
			provide: APP_NAME,
			useValue: App.my,
		},
		{
			provide: APP_SETTINGS_ADAPTER,
			useClass: MyAppSettingsAdapter,
		},
		{
			provide: PLAUSIBLE_DOMAIN,
			useValue: 'my.agilox.net',
		},
		{
			provide: AGILOX_API_ENDPOINT,
			useValue: environment.server,
		},
		UserNotificationsService,
		{
			provide: AUTH_CONFIG,
			useFactory: MSALAUTHConfigFactory,
			deps: [ENVIRONMENT],
		},
		localeProvider,
		PlausibleService,
		provideAppInitializer(() => {
			inject(PlausibleService);
		}),
		{
			provide: SURVICATE_CONFIG,
			useValue: {
				roles: [Role.customer, Role.partner],
				customTraitsNeeded: true,
			},
		},
		SurvicateService,
		provideAppInitializer(() => {
			inject(SurvicateService).initialize();
		}),
		DecimalPipe,
		CustomDatePipe,
		CustomTimePipe,
		CustomNumberPipe,
		...provideNumberFormatter(),
		ToastService,
		SubscriptionDatePipe,
		provideAnimations(),
		FleetEventsService,
	],
};
